/**
 * Global search results — Step 85, the reference's "See more results" page
 * (Kai: "when I hit search... it only searches across tickets"). Enter in
 * the top-bar omnisearch and every group's View-all land HERE, not in the
 * ticket queue. A left rail of entity tabs with live counts (All / Tickets /
 * Service items / Requesters / Agents — Solutions joins at Step 33), up to
 * 25 rows per entity (past that the answer is a narrower term; the tickets
 * tab links to the queue's q filter for the real facets). q and tab live in
 * the URL — a search is shareable.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, LayoutGrid, Search, Ticket, User, Users } from 'lucide-react';
import { ticketsApi } from '../features/tickets/api';
import { CatalogueIcon } from '../features/fields/catalogueIcons';
import { Highlight } from '../components/ui/highlight';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { qk } from '../lib/queryClient';
import { useDebouncedUrlState, useUrlState } from '../lib/urlState';
import { cn } from '../lib/utils';
import { ticketPath } from '../features/tickets/api';

const TABS = [
  { key: 'tickets', label: 'Tickets', icon: Ticket },
  { key: 'items', label: 'Service items', icon: LayoutGrid },
  { key: 'articles', label: 'Solutions', icon: BookOpen },
  { key: 'requesters', label: 'Requesters', icon: User },
  { key: 'agents', label: 'Agents', icon: Users },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function SearchResultsPage() {
  const navigate = useNavigate();
  // The input tracks keystrokes; queries key off the URL's settled value —
  // useDebouncedUrlState pushes local → URL after 300ms, and the second hook
  // reads the same key back, so Back/Forward drive both.
  const [qLive, setQ] = useDebouncedUrlState('q', '', 300);
  const [qSettled] = useUrlState('q', '');
  const [tab, setTab] = useUrlState('tab', 'all');
  const term = qSettled.trim();

  // The grouped call carries every tab's count; the entity call carries the
  // open tab's full 25. Both are cached by (q, entity) so tab-hopping is free.
  const grouped = useQuery({
    queryKey: qk.globalSearch(term),
    queryFn: () => ticketsApi.globalSearch(term),
    enabled: term.length >= 2,
  });
  const entityQuery = useQuery({
    queryKey: qk.globalSearchEntity(term, tab),
    queryFn: () => ticketsApi.globalSearch(term, tab),
    enabled: term.length >= 2 && tab !== 'all',
  });

  const counts: Record<TabKey, number> = {
    tickets: grouped.data?.tickets?.total ?? 0,
    items: grouped.data?.items?.total ?? 0,
    articles: grouped.data?.articles?.total ?? 0,
    requesters: grouped.data?.requesters?.total ?? 0,
    agents: grouped.data?.agents?.total ?? 0,
  };
  const totalAll = counts.tickets + counts.items + counts.articles + counts.requesters + counts.agents;

  const ticketRows = (g: NonNullable<typeof grouped.data>['tickets']) =>
    g!.entries.map((t) => (
      <button
        key={t.id}
        className="block w-full border-b px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-accent"
        onClick={() => navigate(ticketPath(t.type, t.id))}
      >
        <Highlight text={t.subject} term={term} />{' '}
        <span className="text-muted-foreground">
          (#{t.type === 'service_request' ? 'SR' : 'INC'}-{t.number})
        </span>
      </button>
    ));
  const itemRows = (g: NonNullable<typeof grouped.data>['items']) =>
    g!.entries.map((it) => (
      <button
        key={it.id}
        className="flex w-full items-center gap-2.5 border-b px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-accent"
        onClick={() => navigate(`/catalogue?item=${it.id}`)}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
          <CatalogueIcon name={it.icon} className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block truncate">
            <Highlight text={it.name} term={term} />
          </span>
          {it.description && (
            <span className="block truncate text-xs text-muted-foreground">{it.description}</span>
          )}
        </span>
      </button>
    ));
  const articleRows = (g: NonNullable<typeof grouped.data>['articles']) =>
    g!.entries.map((a) => (
      <button
        key={a.id}
        className="flex w-full items-center gap-2.5 border-b px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-accent"
        onClick={() => navigate(`/kb?article=${a.id}`)}
      >
        <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <span className="block truncate">
            <Highlight text={a.title} term={term} />
          </span>
          <span className="block truncate text-xs text-muted-foreground">{a.folderName}</span>
        </span>
      </button>
    ));
  const peopleRows = (g: NonNullable<typeof grouped.data>['requesters']) =>
    g!.entries.map((p) => (
      // Step 53/130 — the person page these were waiting for now exists.
      <button
        key={p.id}
        className="block w-full border-b px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-accent"
        onClick={() => navigate(`/people/${p.id}`)}
      >
        <Highlight text={p.name ?? p.email} term={term} />{' '}
        <span className="text-muted-foreground">
          – <Highlight text={p.email} term={term} />
        </span>
      </button>
    ));

  function section(key: TabKey, group: { entries: unknown[]; total: number } | null | undefined) {
    if (!group || group.total === 0) return null;
    const meta = TABS.find((t) => t.key === key)!;
    return (
      <div key={key}>
        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <meta.icon className="h-3.5 w-3.5" /> {meta.label}
        </p>
        <div className="overflow-hidden rounded-md border bg-card">
          {key === 'tickets' && ticketRows(group as never)}
          {key === 'items' && itemRows(group as never)}
          {key === 'articles' && articleRows(group as never)}
          {(key === 'requesters' || key === 'agents') && peopleRows(group as never)}
          {group.total > group.entries.length && (
            <button
              className="block w-full px-3 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
              onClick={() => setTab(key)}
            >
              View all ({group.total})
            </button>
          )}
        </div>
      </div>
    );
  }

  const activeGroup = tab === 'all' ? null : (entityQuery.data?.[tab as TabKey] ?? null);

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="relative mb-5">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={qLive}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search tickets, services, people…"
          className="h-11 pl-10 text-base"
          autoFocus
        />
      </div>

      {term.length < 2 ? (
        <p className="text-sm text-muted-foreground">Type at least two characters to search.</p>
      ) : (
        <div className="flex gap-6">
          <nav className="w-44 shrink-0 space-y-0.5">
            {[{ key: 'all', label: 'All results', icon: Search } as const, ...TABS].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  tab === t.key
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <t.icon className="h-4 w-4" />
                <span className="flex-1 truncate">{t.label}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {t.key === 'all' ? totalAll : counts[t.key as TabKey]}
                </span>
              </button>
            ))}
          </nav>

          <div className="min-w-0 flex-1 space-y-5">
            {(tab === 'all' ? grouped.isPending : entityQuery.isPending) && (
              <div className="space-y-3">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            )}
            {(tab === 'all' ? grouped.isError : entityQuery.isError) && (
              <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
                <p className="text-sm font-medium text-destructive">Search failed</p>
                <button
                  onClick={() => (tab === 'all' ? grouped.refetch() : entityQuery.refetch())}
                  className="text-xs text-destructive/80 underline hover:text-destructive"
                >
                  Retry
                </button>
              </div>
            )}

            {tab === 'all' && grouped.data && (
              <>
                {totalAll === 0 && (
                  <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No matches for “{term}”.
                  </p>
                )}
                {section('tickets', grouped.data.tickets)}
                {section('items', grouped.data.items)}
                {section('articles', grouped.data.articles)}
                {section('requesters', grouped.data.requesters)}
                {section('agents', grouped.data.agents)}
              </>
            )}

            {tab !== 'all' && entityQuery.data && activeGroup && (
              <div>
                <div className="overflow-hidden rounded-md border bg-card">
                  {tab === 'tickets' && ticketRows(activeGroup as never)}
                  {tab === 'items' && itemRows(activeGroup as never)}
                  {tab === 'articles' && articleRows(activeGroup as never)}
                  {(tab === 'requesters' || tab === 'agents') && peopleRows(activeGroup as never)}
                  {activeGroup.total === 0 && (
                    <p className="p-6 text-center text-sm text-muted-foreground">No matches for “{term}”.</p>
                  )}
                </div>
                {activeGroup.total > 25 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Showing the first 25 of {activeGroup.total} — narrow the search
                    {tab === 'tickets' && (
                      <>
                        , or{' '}
                        <button
                          className="text-brand underline-offset-2 hover:underline"
                          onClick={() => navigate(`/tickets?q=${encodeURIComponent(term)}`)}
                        >
                          open these in the ticket queue
                        </button>{' '}
                        for the full filters
                      </>
                    )}
                    .
                  </p>
                )}
                {tab === 'tickets' && activeGroup.total > 0 && activeGroup.total <= 25 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    <button
                      className="text-brand underline-offset-2 hover:underline"
                      onClick={() => navigate(`/tickets?q=${encodeURIComponent(term)}`)}
                    >
                      Open these in the ticket queue
                    </button>{' '}
                    for the full filters.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
